/**
 * Idiomas do site. O português fica nos endereços de sempre ("/"), inglês em
 * "/en/" e espanhol em "/es/", com endereços traduzidos nas páginas legais.
 *
 * A detecção automática do idioma acontece no servidor (public/.htaccess):
 * 1) escolha salva no cookie LANG_COOKIE; 2) idioma do navegador.
 * Se mudar algum endereço aqui, atualize também as regras do .htaccess.
 */

export const locales = ["pt", "en", "es"] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = "pt";

/** Idiomas que ficam num prefixo (/en/, /es/). */
export const prefixedLocales = ["en", "es"] as const;
export type PrefixedLocale = (typeof prefixedLocales)[number];

export const htmlLang: Record<Locale, string> = { pt: "pt-BR", en: "en", es: "es" };
export const ogLocale: Record<Locale, string> = { pt: "pt_BR", en: "en_US", es: "es_LA" };
export const localeName: Record<Locale, string> = {
  pt: "Português",
  en: "English",
  es: "Español",
};
export const localeShort: Record<Locale, string> = { pt: "PT", en: "EN", es: "ES" };

/** Cookie com a escolha manual do seletor (lido pelo .htaccess). */
export const LANG_COOKIE = "mp_lang";

export type PageKey = "home" | "privacy" | "terms" | "contact" | "deleteAccount";
export type LegalPageKey = Exclude<PageKey, "home">;

export const slugs: Record<Locale, Record<LegalPageKey, string>> = {
  pt: {
    privacy: "privacidade",
    terms: "termos",
    contact: "contato",
    deleteAccount: "excluir-conta",
  },
  en: {
    privacy: "privacy",
    terms: "terms",
    contact: "contact",
    deleteAccount: "delete-account",
  },
  es: {
    privacy: "privacidad",
    terms: "terminos",
    contact: "contacto",
    deleteAccount: "eliminar-cuenta",
  },
};

export const legalPageKeys = Object.keys(slugs.pt) as LegalPageKey[];

export function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

export function isPrefixedLocale(value: string): value is PrefixedLocale {
  return (prefixedLocales as readonly string[]).includes(value);
}

/** Endereço de uma página num idioma. Ex.: pathFor("en", "privacy") → "/en/privacy/". */
export function pathFor(locale: Locale, page: PageKey = "home", hash?: string): string {
  const base = locale === defaultLocale ? "/" : `/${locale}/`;
  const path = page === "home" ? base : `${base}${slugs[locale][page]}/`;
  return hash ? `${path}#${hash}` : path;
}

/** Descobre idioma e página a partir de um endereço (usado pelo seletor). */
export function parsePath(pathname: string): { locale: Locale; page: PageKey } {
  const parts = pathname.split("/").filter(Boolean);
  const locale: Locale = parts[0] && isLocale(parts[0]) && parts[0] !== defaultLocale ? parts[0] : defaultLocale;
  const slug = locale === defaultLocale ? parts[0] : parts[1];
  const page = (Object.entries(slugs[locale]) as [LegalPageKey, string][]).find(
    ([, s]) => s === slug,
  )?.[0];
  return { locale, page: page ?? "home" };
}

/** Endereços da mesma página em todos os idiomas (hreflang e sitemap). */
export function alternatesFor(page: PageKey): Record<string, string> {
  return {
    "pt-BR": pathFor("pt", page),
    en: pathFor("en", page),
    es: pathFor("es", page),
    "x-default": pathFor("pt", page),
  };
}
